import React, { useEffect } from "react";
import {
    FaCheckCircle,
    FaCertificate,
    FaRulerCombined,
    FaBoxes,
    FaFlask,
    FaHandshake,
    FaFileAlt,
    FaBoxOpen,
    FaChartLine,
} from "react-icons/fa";

/* Small, descriptive constants */
const sections = [
    { id: "overview", title: "Overview of Our Quality Commitment", icon: <FaCheckCircle />, color: "#10b981" },
    { id: "certifications", title: "Certifications & Quality Compliance", icon: <FaCertificate />, color: "#f59e0b" },
    { id: "incoming", title: "Incoming Quality Inspection", icon: <FaRulerCombined />, color: "#06b6d4" },
    { id: "predispatch", title: "Pre-Dispatch Quality Control", icon: <FaBoxOpen />, color: "#7c3aed" },
    { id: "testing", title: "Testing Facilities", icon: <FaFlask />, color: "#ef4444" },
    { id: "suppliers", title: "Supplier Qualification & Audits", icon: <FaHandshake />, color: "#0ea5a4" },
    { id: "docs", title: "Quality Documentation Provided", icon: <FaFileAlt />, color: "#2563eb" },
    { id: "packaging", title: "Packaging & Handling Quality", icon: <FaBoxes />, color: "#f97316" },
    { id: "improvement", title: "Continuous Improvement", icon: <FaChartLine />, color: "#06b6d4" },
];

/* small square badge images (replace with optimized small images for best result) */
const badgeImages = {
    certifications: "https://res.cloudinary.com/dbirahuue/image/upload/v1765425135/Certifications_zec1np.webp",
    incoming: "https://res.cloudinary.com/dbirahuue/image/upload/v1765425135/IncomingQA_eek42p.webp",
    predispatch: "https://res.cloudinary.com/dbirahuue/image/upload/v1765425136/Pre-DispatchQA_rrjw1m.webp",
    testing: "https://res.cloudinary.com/dbirahuue/image/upload/v1765426282/TestingFacilities_j6gaop.webp",
    docs: "https://res.cloudinary.com/dbirahuue/image/upload/v1765426281/QualityDocumentation_rtnbxs.webp",
    suppliers: "https://res.cloudinary.com/dbirahuue/image/upload/v1765426277/Audits_rhs00e.webp",
    packaging: "https://res.cloudinary.com/dbirahuue/image/upload/v1765426278/Packaging_zye54l.webp",
    improvement: "https://res.cloudinary.com/dbirahuue/image/upload/v1765426278/ContinuousImprovement_tekpyj.webp",
    overview: "https://res.cloudinary.com/dbirahuue/image/upload/v1765423734/Overview_xayizq.webp",
};

/* ---------- Small presentational chunks ---------- */

/* Info card / hero brief (overview) */
const InfoCards = () => (
    <div className="qa-container mx-3 mt-3">
        <div className="qa-card backGround mb-4 d-flex flex-column flex-lg-row gap-3" id="overview-brief" data-testid="info-cards-intro">
            <div className="qa-icon-wrap d-flex justify-content-center justify-content-lg-start mb-3 mb-lg-0">
                <div className="qa-icon-badge" style={{ background: sections[0].color }} aria-hidden="true">
                    <img src={badgeImages.overview} alt="Vibha Enterprises logo" className="qa-icon-img" />
                </div>
            </div>

            <div className="qa-card-body text-justify">
                <div className="qa-title">1. Overview of Our Quality Commitment</div>
                <div className="qa-sub">
                    At <strong>Vibha Enterprises, Pune</strong>, we believe that uncompromised quality is the key to customer trust. As a leading importer
                    and supplier of industrial fasteners, we ensure every product meets global standards of strength, durability, and performance.
                </div>
                <div className="qa-detail">
                    <strong>Our commitment is centered on:</strong>
                    <ul className="qa-list">
                        <li>Zero-defect supply</li>
                        <li>On-time delivery</li>
                        <li>Strict compliance with international norms</li>
                        <li>Customer satisfaction at every stage</li>
                    </ul>
                </div>
            </div>
        </div>
    </div>
);

/* SectionCard: circular badge contains a small image (imageIcon) */
const SectionCard = ({ id, color, imageIcon, title, children }) => (
    <div id={id} className="qa-card backGround mb-4">
        <div className="d-flex flex-column flex-lg-row align-items-center align-items-lg-start gap-3">
            <div className="qa-icon-wrap d-flex justify-content-center justify-content-lg-start">
                <div className="qa-icon-badge" style={{ background: color }}>
                    {imageIcon ? <img src={imageIcon} alt={`${title} icon`} className="qa-icon-img" /> : <span aria-hidden />}
                </div>
            </div>

            <div className="qa-card-body text-start text-justify">
                <div className="qa-title">{title}</div>
                <div className="qa-sub">{children}</div>
            </div>
        </div>
    </div>
);

/* MiniTOC with colored small badges */
const MiniTOC = ({ sections }) => (
    <aside className="qa-toc" data-testid="mini-toc">
        <div className="qa-card qa-toc-card">
            <div className="qa-toc-title">On this page</div>

            <nav>
                {sections.map((s) => (
                    <a key={s.id} className="qa-toc-link" href={`#${s.id}`}>
                        <div className="qa-toc-item d-flex align-items-center gap-2">
                            <div className="qa-toc-icon-badge" style={{ background: s.color }}>
                                {s.icon}
                            </div>
                            <div className="qa-toc-text">{s.title}</div>
                        </div>
                    </a>
                ))}
            </nav>

            <hr />
            <a href="/contact" className="btn btn-brand w-100">Contact Sales</a>
        </div>
    </aside>
);

/* main component */
const QualityAssurance = () => {
    useEffect(() => {
        const obs = new IntersectionObserver(
            (entries) => entries.forEach((e) => e.isIntersecting && e.target.classList.add("qa-animate-in")),
            { threshold: 0.12 }
        );
        const cards = document.querySelectorAll(".qa-card");
        cards.forEach((el) => obs.observe(el));
        return () => obs.disconnect();
    }, []);

    const renderBanner = () => (
        <div className="position-relative bg-secondary" style={{ overflow: "hidden" }}>
            <img
                src="https://res.cloudinary.com/dbirahuue/image/upload/v1765422653/QualityAssurance_hsiacp.webp"
                className="img-fluid w-100"
                alt="Banner"
                style={{
                    objectFit: "cover",
                    maxHeight: "600px",
                    width: "100%",
                    mixBlendMode: "multiply",
                }}
            />
            <div
                className="d-flex flex-column justify-content-center align-items-center position-absolute top-0 start-0 w-100 h-100"
            >
                {/* Large screens */}
                <h2 className="d-none d-lg-block text-white text-center text-uppercase p-2  bg-secondary bg-opacity-50" data-aos="zoom-in" style={{letterSpacing: "5px"}}>Vibha Enterprises</h2>

                {/* Small/medium screens */}
                <h4 className="d-block d-lg-none p-2 text-white text-center text-uppercase bg-secondary bg-opacity-50" data-aos="zoom-in" style={{letterSpacing: "5px"}}>Vibha Enterprises</h4>
            </div>
        </div>
    );

    const renderMainSections = () => (
        <>
            <SectionCard id="certifications" color="#f59e0b" imageIcon={badgeImages.certifications} title="2. Certifications & Quality Compliance">
                All products sourced by Vibha Enterprises are manufactured under recognized quality systems and meet essential compliance standards.
                <div className="qa-detail">
                    <strong>Quality Compliance Includes:</strong>
                    <ul>
                        <li>ISO 9001-certified manufacturing partners</li>
                        <li>Compliance with DIN, ISO, ASTM, BS, and JIS standards</li>
                    </ul>
                </div>
                This ensures reliability and transparency for every supply.
            </SectionCard>

            <SectionCard id="incoming" color="#06b6d4" imageIcon={badgeImages.incoming} title="3. Incoming Quality Inspection">
                Every batch received undergoes a rigorous quality check before entering our inventory.
                <div className="qa-detail">
                    <strong>Inspection Parameters:</strong>
                    <ul>
                        <li>Dimensional accuracy (using vernier, micrometers, gauges)</li>
                        <li>Thread check with GO/NO-GO gauges</li>
                        <li>Coating thickness measurement-(via accredited labs when needed)</li>
                        <li>Visual and surface defect inspection</li>
                        <li>Chemical Testing by Wet Analysis or optical emission Spectrometer-(via accredited labs when needed)</li>
                        <li>Only approved materials are forwarded to storage and dispatch.</li>
                    </ul>
                </div>
            </SectionCard>

            <SectionCard id="predispatch" color="#7c3aed" imageIcon={badgeImages.predispatch} title="4. Pre-Dispatch Quality Control">
                Before dispatch, each order is verified to ensure consistency and compliance with customer specifications.
                <div className="qa-detail">
                    <strong>Pre-Dispatch Checks Include:</strong>
                    <ul>
                        <li>Sampling inspection</li>
                        <li>Surface finish and plating quality check</li>
                        <li>Packaging integrity, labeling & batch identification</li>
                    </ul>
                </div>
                This ensures customers receive ready-to-use, fully compliant fasteners.
            </SectionCard>

            <SectionCard id="testing" color="#ef4444" imageIcon={badgeImages.testing} title="5. Testing Facilities">
                We utilize a combination of in-house testing tools and NABL-accredited laboratories for advanced testing.
                <div className="qa-detail">
                    <strong>Testing Capabilities:</strong>
                    <ul>
                        <li>Tensile testing (external facility)</li>
                        <li>Coating thickness measurement (third-party labs)</li>
                        <li>Salt spray testing for coated fasteners (external facility)</li>
                    </ul>
                </div>
                These tests validate performance under actual industrial conditions.
            </SectionCard>

            <SectionCard id="suppliers" color="#0ea5a4" imageIcon={badgeImages.suppliers} title="6. Supplier Qualification & Audits">
                We work only with globally reputed manufacturers who pass our multi-stage supplier evaluation process. Only approved suppliers are included in our vendor network.
            </SectionCard>

            <SectionCard id="docs" color="#2563eb" imageIcon={badgeImages.docs} title="7. Quality Documentation Provided">
                We maintain professional documentation practices to support customer audits and technical approvals.
                <div className="qa-detail">
                    <strong>Documents Offered:</strong>
                    <ul>
                        <li>Dimensional inspection reports</li>
                        <li>Coating and plating reports</li>
                        <li>Material certificates on request</li>
                    </ul>
                </div>
            </SectionCard>

            <SectionCard id="packaging" color="#f97316" imageIcon={badgeImages.packaging} title="8. Packaging & Handling Quality">
                To ensure product protection throughout storage and transportation, we use high-grade packaging solutions.
                <div className="qa-detail">
                    <strong>Packaging Features:</strong>
                    <ul>
                        <li>Rust-preventive & moisture-protected packing</li>
                    </ul>
                </div>
                This ensures your fasteners arrive clean, damage-free, and ready for use.
            </SectionCard>

            <SectionCard id="improvement" color="#06b6d4" imageIcon={badgeImages.improvement} title="9. Continuous Improvement">
                Quality at Vibha Enterprises is an evolving process supported by continuous improvement initiatives.
                <div className="qa-detail">
                    <strong>Our Improvement Practices:</strong>
                    <ul>
                        <li>Customer feedback analysis</li>
                        <li>Corrective & Preventive Actions (CAPA)</li>
                        <li>Regular training for quality & warehouse teams</li>
                    </ul>
                </div>
                We consistently upgrade our processes to deliver better value.
            </SectionCard>
        </>
    );

    return (
        <div className="container-fluid p-0">
            {/* banner */}
            {renderBanner()}

            {/* intro */}
            <InfoCards />

            {/* main content */}
            <div className="qa-container mx-3">
                <div className="qa-grid">
                    <div>
                        {renderMainSections()}
                        <div className="qa-bottom-cta mb-3">
                            <div className="qa-cta-text">Need inspection reports, MTCs or technical sheets? We can share all required documents for audits & approvals.</div>
                            <div><a href="/contact" className="btn btn-brand">Request Documents</a></div>
                        </div>
                    </div>

                    <MiniTOC sections={sections} />
                </div>
            </div>
        </div>
    );
};

export default QualityAssurance;
